import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login as apiLogin, login2fa as apiLogin2fa, getPlatformStatus } from '@/api/auth';
import { useAuth } from '@/auth/store';
import BrandLogo from '@/shared/components/BrandLogo';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuth((s) => s.setSession);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  // Підтримка ?return_to= після успішного login (наприклад accept-invitation).
  const returnTo = params.get('return_to');
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Platform Admin Блок A — 2FA. Коли login повертає requires_2fa, показуємо
  // другий екран вводу TOTP-коду. mfa_token — одноразовий токен з кроку 1.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  /**
   * Завершення логіну: зберігаємо токени + скидаємо застарілий tenant +
   * редирект. Спільне для звичайного логіну і другого кроку 2FA.
   */
  async function finishLogin(res: { user_id?: string | null }) {
    // ч.1397: cookie вже поставив сервер; тут — лише ознака сесії і користувач.
    setSession(res.user_id ?? null);
    // BUG-FIX (3.0.5.1b → fix): scrub застарілий activeTenantId з
    // попередньої сесії. Без цього axios interceptor додавав би старий
    // X-Tenant-Id у /auth/me, backend → 403 «No active membership»,
    // 403-handler робив би full reload на /select-tenant?reason=membership_lost.
    setActiveTenant(null);
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    // Platform Admin Блок A: super-admin (власник сервісу) — БЕЗ tenant'ів.
    // Ведемо одразу у /platform, інакше /select-tenant → /no-tenants (тупик).
    try {
      const ps = await getPlatformStatus();
      if (ps.is_platform_owner) {
        navigate('/platform', { replace: true });
        return;
      }
      // ч.1319: редактор відео-інструкцій працює лише в розділі відео панелі.
      if (ps.is_video_editor) {
        navigate('/platform/video', { replace: true });
        return;
      }
    } catch {
      // platform-status недоступний — звичайний flow нижче.
    }
    // Звичайний tenant-select flow (0→/no-tenants, 1→авто, N→UI).
    navigate('/select-tenant', { replace: true });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await apiLogin(loginValue, password);
      if (res.requires_2fa) {
        // Крок 1 пройдено — потрібен TOTP-код. Переходимо на другий екран.
        setMfaToken(res.mfa_token);
        setCode('');
      } else if (res.session === 'cookie' || (res.access_token && res.refresh_token)) {
        await finishLogin(res);
      } else {
        setError(t('auth.invalid_credentials'));
      }
    } catch {
      setError(t('auth.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit2fa(e: FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setLoading(true);
    try {
      const tok = await apiLogin2fa(mfaToken, code.trim());
      await finishLogin(tok);
    } catch {
      setError('Невірний код. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  }

  // Другий екран: ввід 6-значного коду з Google Authenticator.
  if (mfaToken) {
    return (
      <div className="login">
        <form onSubmit={onSubmit2fa}>
          <h1>Двофакторна автентифікація</h1>
          <p style={{ marginTop: 0, marginBottom: '1.25rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Введіть 6-значний код із Google Authenticator.
          </p>
          <div className="field">
            <label>Код підтвердження</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              autoFocus
              required
            />
          </div>
          <button
            type="submit"
            className="primary"
            style={{ width: '100%' }}
            disabled={loading || code.trim().length < 6}
          >
            {loading ? 'Перевірка…' : 'Підтвердити'}
          </button>
          {error && <div className="error">{error}</div>}
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
            Немає доступу до коду?{' '}
            <Link to={`/account-recovery/2fa?email=${encodeURIComponent(loginValue.trim())}`}>
              Відновити вхід
            </Link>
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => {
                setMfaToken(null);
                setCode('');
                setError(null);
              }}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0 }}
            >
              ← Назад до входу
            </button>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit}>
        <h1 className="login__brand"><BrandLogo variant="full" /></h1>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={loginValue}
            onChange={(e) => setLoginValue(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label>{t('auth.password')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <button type="submit" className="primary" style={{ width: '100%' }} disabled={loading}>
          {loading ? t('auth.signing_in') : t('auth.sign_in')}
        </button>
        {error && <div className="error">{error}</div>}
        <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
          <Link to={`/forgot-password${loginValue.trim()
            ? `?email=${encodeURIComponent(loginValue.trim())}` : ''}`}>
            Забули пароль?
          </Link>
        </p>
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', textAlign: 'center' }}>
          Ще немає акаунта? <Link to="/signup">Зареєструватися</Link>
        </p>
      </form>
    </div>
  );
}
